import { useAuthStore } from '@/modules/auth/stores/auth.store';
import { watch } from 'vue';
import { useFeaturesStore } from '../stores';

/**
 * Composable para inicializar el sistema de features después del login.
 * Debe usarse en App.vue o en un layout global.
 *
 * @author Omar Sánchez Martínez
 * @since 2025-11-28
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useGlobalFeatures } from '@/app/features';
 *
 * // Inicializa el sistema de features
 * useGlobalFeatures();
 * </script>
 * ```
 */
export const useGlobalFeatures = (): void => {
  const authStore = useAuthStore();
  const featuresStore = useFeaturesStore();

  // Watch para detectar cambios en autenticación
  watch(
    () => authStore.isAuthenticated,
    async (isAuth, wasAuth) => {
      // En el primer render con immediate, wasAuth es undefined
      // Si el usuario ya está autenticado, cargar features
      if (isAuth && !featuresStore.isLoaded) {
        // Usuario autenticado y features no cargados - cargar features pendientes
        await featuresStore.fetchPendingFeatures();
      } else if (!isAuth && wasAuth) {
        // Usuario cerró sesión - limpiar store
        featuresStore.$reset();
      }
    },
    { immediate: true },
  );
};
